import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, MapPin, Upload, Download, FileSpreadsheet, Eye, Search, Grid3x3, List, LayoutGrid, Building2, Calendar } from 'lucide-react';

const CenterManagement = () => {
  const [centers, setCenters] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCenter, setEditingCenter] = useState(null);
  const [importDialog, setImportDialog] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [updateExisting, setUpdateExisting] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewInfoDialog, setViewInfoDialog] = useState(false);
  const [selectedCenterInfo, setSelectedCenterInfo] = useState(null);
  const [formData, setFormData] = useState({ 
    name: '', 
    location: '', 
    address: '',
    city: '',
    state: '',
    postal_code: '',
    country: '',
    phone_number: '',
    learning_mode: '',
    registration_date: null,
    renewal_date: null,
    status: 'active',
    description: '', 
    center_head_id: '' 
  });

  useEffect(() => {
    loadCenters();
    loadTeachers();
  }, []);

  // Auto-calculate renewal date when registration date changes
  useEffect(() => {
    if (formData.registration_date) {
      const regDate = new Date(formData.registration_date);
      if (!isNaN(regDate.getTime())) {
        // Add 3 years
        const renewalDate = new Date(regDate);
        renewalDate.setFullYear(renewalDate.getFullYear() + 3);
        setFormData(prev => ({
          ...prev,
          renewal_date: renewalDate.toISOString().split('T')[0]
        }));
      }
    }
  }, [formData.registration_date]);

  const loadCenters = async () => {
    try {
      const response = await axios.get(`${API}/centers`);
      setCenters(response.data);
    } catch (error) {
      toast.error('Failed to load centers');
    }
  };

  const loadTeachers = async () => {
    try {
      const response = await axios.get(`${API}/users?role=teacher`);
      setTeachers(response.data);
    } catch (error) {
      console.error('Failed to load teachers');
    }
  };

  const filteredCenters = centers.filter(center => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      center.name?.toLowerCase().includes(term) ||
      center.location?.toLowerCase().includes(term) ||
      center.city?.toLowerCase().includes(term) ||
      center.state?.toLowerCase().includes(term)
    );
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        name: formData.name,
        location: formData.location,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        postal_code: formData.postal_code,
        country: formData.country,
        phone_number: formData.phone_number,
        learning_mode: formData.learning_mode,
        registration_date: formData.registration_date,
        renewal_date: formData.renewal_date,
        status: formData.status,
        description: formData.description,
        center_head_id: formData.center_head_id && formData.center_head_id !== 'none' ? formData.center_head_id : null
      };
      
      if (editingCenter) {
        await axios.put(`${API}/centers/${editingCenter.id}`, submitData);
        toast.success('Center updated successfully');
      } else {
        await axios.post(`${API}/centers`, submitData);
        toast.success('Center created successfully');
      }
      setIsDialogOpen(false);
      setFormData({ 
        name: '', 
        location: '', 
        address: '',
        city: '',
        state: '',
        postal_code: '',
        country: '',
        phone_number: '',
        learning_mode: '',
        registration_date: null,
        renewal_date: null,
        status: 'active',
        description: '', 
        center_head_id: '' 
      });
      setEditingCenter(null);
      loadCenters();
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleEdit = (center) => {
    setEditingCenter(center);
    setFormData({ 
      name: center.name, 
      location: center.location, 
      address: center.address || '',
      city: center.city || '',
      state: center.state || '',
      postal_code: center.postal_code || '',
      country: center.country || '',
      phone_number: center.phone_number || '',
      learning_mode: center.learning_mode || '',
      registration_date: center.registration_date || null,
      renewal_date: center.renewal_date || null,
      status: center.status || 'active',
      description: center.description || '', 
      center_head_id: center.center_head_id || '' 
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (centerId) => {
    if (!window.confirm('Are you sure you want to delete this center?')) return;
    try {
      await axios.delete(`${API}/centers/${centerId}`);
      toast.success('Center deleted successfully');
      loadCenters();
    } catch (error) {
      toast.error('Failed to delete center');
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      toast.error('Please select a file to import');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('file', importFile);
      
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/centers/import/excel?update_existing=${updateExisting}`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success(response.data.message);
      loadCenters();
      setImportDialog(false);
      setImportFile(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import centers');
    }
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/centers/export/excel`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'centers.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Centers exported successfully');
    } catch (error) {
      toast.error('Failed to export centers');
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="center-management">
      <Card className="p-6 shadow-xl border-0 bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Center Management
            </h2>
            <p className="text-sm text-gray-600 mt-1">Manage all learning centers and their details</p>
          </div>
          
          <div className="flex items-center gap-3 flex-wrap">
            {/* View Mode Toggle */}
            <div className="flex items-center gap-1 bg-white rounded-lg p-1 shadow-sm border">
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded transition-all ${
                  viewMode === 'list' 
                    ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-md' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                title="List View"
              >
                <List className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded transition-all ${
                  viewMode === 'grid' 
                    ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-md' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                title="Grid View"
              >
                <Grid3x3 className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('block')}
                className={`p-2 rounded transition-all ${
                  viewMode === 'block' 
                    ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-md' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                title="Block View"
              >
                <LayoutGrid className="w-4 h-4" />
              </button>
            </div>

            <Button
              onClick={() => setImportDialog(true)}
              variant="outline"
              className="flex items-center gap-2"
            >
              <Upload className="w-4 h-4" />
              Import
            </Button>
            <Button
              onClick={handleExport}
              variant="outline"
              className="flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Export
            </Button>
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button className="flex items-center space-x-2 bg-gradient-to-r from-blue-500 to-indigo-600" data-testid="add-center-button">
                  <Plus className="w-4 h-4" />
                  <span>Add Center</span>
                </Button>
              </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="center-dialog">
              <DialogHeader>
                <DialogTitle>{editingCenter ? 'Edit Center' : 'Add New Center'}</DialogTitle>
              </DialogHeader>
              
              <div className="bg-blue-50 p-3 rounded-lg border border-blue-200 mb-4">
                <p className="text-xs text-blue-900">
                  <strong>📦 Shipping Info Required:</strong> Complete address, city, state, postal code, and phone are mandatory for kit delivery to centers.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="name">Center Name</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    data-testid="center-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="location">Location</Label>
                  <Input
                    id="location"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    required
                    placeholder="e.g., New York, NY"
                    data-testid="center-location-input"
                  />
                </div>
                
                <div>
                  <Label htmlFor="address">Full Address *</Label>
                  <Input
                    id="address"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="Complete address for shipping"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="city">City *</Label>
                    <Input
                      id="city"
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      placeholder="City"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="state">State *</Label>
                    <Input
                      id="state"
                      value={formData.state}
                      onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                      placeholder="State"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="postal_code">Postal Code / Pincode *</Label>
                    <Input
                      id="postal_code"
                      value={formData.postal_code}
                      onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                      placeholder="Enter pincode"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="country">Country</Label>
                    <Input
                      id="country"
                      value={formData.country}
                      onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                      placeholder="Country"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="phone_number">Phone Number *</Label>
                    <Input
                      id="phone_number"
                      value={formData.phone_number}
                      onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                      placeholder="10-digit phone number"
                      required
                      maxLength={10}
                    />
                  </div>
                  <div></div>
                </div>

                <div>
                  <Label htmlFor="learning_mode">Learning Mode</Label>
                  <Select
                    value={formData.learning_mode}
                    onValueChange={(value) => setFormData({ ...formData, learning_mode: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select learning mode" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="online">Online</SelectItem>
                      <SelectItem value="offline">Offline</SelectItem>
                      <SelectItem value="both">Both</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="registration_date">
                      Registration Date *
                    </Label>
                    <Input
                      id="registration_date"
                      type="date"
                      value={formData.registration_date ? new Date(formData.registration_date).toISOString().split('T')[0] : ''}
                      onChange={(e) => setFormData({ ...formData, registration_date: e.target.value ? new Date(e.target.value).toISOString() : null })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="renewal_date" className="flex items-center gap-2">
                      Renewal Date (Auto-calculated)
                      <Calendar className="w-3 h-3 text-blue-500" />
                    </Label>
                    <Input
                      id="renewal_date"
                      type="date"
                      value={formData.renewal_date ? new Date(formData.renewal_date).toISOString().split('T')[0] : ''}
                      readOnly
                      className="bg-blue-50 cursor-not-allowed"
                      title="Auto-calculated as 3 years after registration date"
                    />
                    <p className="text-xs text-blue-600 mt-1">
                      📅 Auto: Registration Date + 3 years
                    </p>
                  </div>
                </div>

                <div>
                  <Label htmlFor="status">Center Status</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(value) => setFormData({ ...formData, status: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                      <SelectItem value="suspended">Suspended</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    data-testid="center-description-input"
                  />
                </div>
                <div>
                  <Label htmlFor="center_head_id">Center Head</Label>
                  <Select
                    value={formData.center_head_id}
                    onValueChange={(value) => setFormData({ ...formData, center_head_id: value })}
                  >
                    <SelectTrigger data-testid="center-head-select">
                      <SelectValue placeholder="Select a center head" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No center head</SelectItem>
                      {teachers.map(teacher => (
                        <SelectItem key={teacher.id} value={teacher.id}>
                          {teacher.name} ({teacher.email})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button type="submit" className="w-full" data-testid="submit-center-button">
                  {editingCenter ? 'Update' : 'Create'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              type="text"
              placeholder="Search centers by name, location, city, or state..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 py-3 w-full border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
            />
          </div>
          {searchTerm && (
            <p className="text-sm text-gray-600 mt-2 font-medium">
              Found {filteredCenters.length} result(s)
            </p>
          )}
        </div>

        {/* Grid View */}
        {viewMode === 'grid' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredCenters.map(center => (
            <Card key={center.id} className="p-5 border hover:shadow-xl transition-all duration-300 bg-gradient-to-br from-white to-gray-50" data-testid={`center-${center.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white shadow-lg">
                      <Building2 className="w-6 h-6" />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900">{center.name}</h4>
                      <p className="text-sm text-gray-500">{center.location}</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="space-y-2 mb-3">
                {center.city && center.state && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Location:</span>
                    <span className="font-semibold text-gray-900">{center.city}, {center.state}</span>
                  </div>
                )}
                {center.phone_number && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Phone:</span>
                    <span className="font-semibold text-gray-900">{center.phone_number}</span>
                  </div>
                )}
                {center.renewal_date && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Renewal:</span>
                    <span className="font-semibold text-gray-900">{new Date(center.renewal_date).toLocaleDateString()}</span>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2 flex-wrap mb-3">
                <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                  center.status === 'active' ? 'bg-emerald-100 text-emerald-700' :
                  center.status === 'suspended' ? 'bg-red-100 text-red-700' :
                  'bg-gray-100 text-gray-700'
                }`}>
                  {center.status || 'active'}
                </span>
                {center.learning_mode && (
                  <span className="px-3 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-700">
                    {center.learning_mode === 'both' ? 'Online & Offline' : center.learning_mode}
                  </span>
                )}
              </div>

              <div className="flex gap-2">
                <Button onClick={() => { setSelectedCenterInfo(center); setViewInfoDialog(true); }} variant="outline" size="sm" className="flex-1">
                  <Eye className="w-4 h-4 mr-1" />
                  View
                </Button>
                <Button onClick={() => handleEdit(center)} variant="outline" size="sm" className="flex-1">
                  <Edit className="w-4 h-4 mr-1" />
                  Edit
                </Button>
                <Button onClick={() => handleDelete(center.id)} variant="outline" size="sm" className="text-red-600 hover:bg-red-50">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </Card>
          ))}
          </div>
        )}

        {/* List View */}
        {viewMode === 'list' && (
          <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white">
                <tr>
                  <th className="px-6 py-3 text-left text-sm font-semibold">Name</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold">Location</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold">Phone</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold">Learning Mode</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold">Status</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold">Renewal Date</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredCenters.map((center, idx) => (
                  <tr key={center.id} className={`hover:bg-gray-50 transition-colors ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`} data-testid={`center-${center.id}`}>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold">
                          <Building2 className="w-5 h-5" />
                        </div>
                        <span className="font-semibold text-gray-900">{center.name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {center.city && center.state ? `${center.city}, ${center.state}` : center.location}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">{center.phone_number || 'N/A'}</td>
                    <td className="px-6 py-4 text-sm text-gray-900 capitalize">
                      {center.learning_mode === 'both' ? 'Online & Offline' : center.learning_mode || 'N/A'}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                        center.status === 'active' ? 'bg-emerald-100 text-emerald-700' :
                        center.status === 'suspended' ? 'bg-red-100 text-red-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {center.status || 'active'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {center.renewal_date ? new Date(center.renewal_date).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-center gap-2">
                        <button onClick={() => { setSelectedCenterInfo(center); setViewInfoDialog(true); }} className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors" title="View Info">
                          <Eye className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleEdit(center)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Edit">
                          <Edit className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleDelete(center.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Delete">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Block View */}
        {viewMode === 'block' && (
          <div className="grid grid-cols-1 gap-3">
            {filteredCenters.map(center => (
              <Card key={center.id} className="p-4 border hover:shadow-md transition-all duration-200" data-testid={`center-${center.id}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 flex-1">
                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white shadow-lg">
                      <Building2 className="w-7 h-7" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-bold text-gray-900 text-lg">{center.name}</h4>
                      <p className="text-sm text-gray-500">{center.location}</p>
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        {center.city && <span className="text-xs bg-gray-100 px-2 py-1 rounded">{center.city}, {center.state}</span>}
                        {center.phone_number && <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">📞 {center.phone_number}</span>}
                        {center.renewal_date && <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">📅 Renewal: {new Date(center.renewal_date).toLocaleDateString()}</span>}
                        <span className={`text-xs px-2 py-1 rounded ${
                          center.status === 'active' ? 'bg-emerald-100 text-emerald-700' :
                          center.status === 'suspended' ? 'bg-red-100 text-red-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {center.status || 'active'}
                        </span>
                        {center.learning_mode && (
                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                            {center.learning_mode === 'both' ? 'Online & Offline' : center.learning_mode}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={() => { setSelectedCenterInfo(center); setViewInfoDialog(true); }} variant="outline" size="sm">
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button onClick={() => handleEdit(center)} variant="outline" size="sm">
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button onClick={() => handleDelete(center.id)} variant="outline" size="sm" className="text-red-600 hover:bg-red-50">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {filteredCenters.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">
              {searchTerm ? 'No centers found matching your search' : 'No centers added yet'}
            </p>
          </div>
        )}
      </Card>

      {/* Import Dialog */}
      <Dialog open={importDialog} onOpenChange={setImportDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Import Centers from Excel</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Select Excel File</Label>
              <div className="mt-2">
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={(e) => setImportFile(e.target.files[0])}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              {importFile && (
                <p className="text-sm text-gray-600 mt-2">
                  Selected: {importFile.name}
                </p>
              )}
            </div>

            <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-md">
              <input
                type="checkbox"
                checked={updateExisting}
                onChange={(e) => setUpdateExisting(e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                id="update-existing-centers-checkbox"
              />
              <Label htmlFor="update-existing-centers-checkbox" className="text-sm cursor-pointer">
                Update existing centers if name matches
              </Label>
            </div>

            <div className="bg-blue-50 p-3 rounded-md">
              <p className="text-xs text-blue-800">
                <strong>Note:</strong> If "Update existing" is checked, centers with matching names will be updated. 
                Otherwise, duplicate names will be skipped.
              </p>
            </div>

            <div className="flex gap-2 justify-end">
              <Button onClick={() => { setImportDialog(false); setImportFile(null); }} variant="outline">
                Cancel
              </Button>
              <Button onClick={handleImport} disabled={!importFile} className="bg-blue-600 hover:bg-blue-700">
                <Upload className="w-4 h-4 mr-2" />
                Import
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Info Dialog */}
      <Dialog open={viewInfoDialog} onOpenChange={setViewInfoDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Center Information</DialogTitle>
          </DialogHeader>
          {selectedCenterInfo && (
            <div className="space-y-4">
              <div className="flex items-center gap-4 pb-4 border-b">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white shadow-lg">
                  <Building2 className="w-8 h-8" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">{selectedCenterInfo.name}</h3>
                  <p className="text-gray-600">{selectedCenterInfo.location}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">City</p>
                  <p className="font-semibold">{selectedCenterInfo.city || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">State</p>
                  <p className="font-semibold">{selectedCenterInfo.state || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Country</p>
                  <p className="font-semibold">{selectedCenterInfo.country || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Phone Number</p>
                  <p className="font-semibold">{selectedCenterInfo.phone_number || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Learning Mode</p>
                  <p className="font-semibold capitalize">
                    {selectedCenterInfo.learning_mode === 'both' ? 'Online & Offline' : selectedCenterInfo.learning_mode || 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Status</p>
                  <span className={`px-3 py-1 text-xs font-semibold rounded-full inline-block ${
                    selectedCenterInfo.status === 'active' ? 'bg-emerald-100 text-emerald-700' :
                    selectedCenterInfo.status === 'suspended' ? 'bg-red-100 text-red-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {selectedCenterInfo.status || 'active'}
                  </span>
                </div>
                {selectedCenterInfo.registration_date && (
                  <div>
                    <p className="text-sm text-gray-600">Registration Date</p>
                    <p className="font-semibold">{new Date(selectedCenterInfo.registration_date).toLocaleDateString()}</p>
                  </div>
                )}
                {selectedCenterInfo.renewal_date && (
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <p className="text-sm text-blue-600 font-medium">Renewal Date</p>
                    <p className="font-bold text-blue-900">{new Date(selectedCenterInfo.renewal_date).toLocaleDateString()}</p>
                    <p className="text-xs text-blue-600 mt-1">📅 3 years from registration</p>
                  </div>
                )}
                {selectedCenterInfo.center_head_id && (
                  <div className="col-span-2">
                    <p className="text-sm text-gray-600">Center Head</p>
                    <p className="font-semibold text-green-700">
                      {teachers.find(t => t.id === selectedCenterInfo.center_head_id)?.name || 'N/A'}
                    </p>
                  </div>
                )}
              </div>
              {selectedCenterInfo.address && (
                <div>
                  <p className="text-sm text-gray-600">Full Address</p>
                  <p className="font-semibold">{selectedCenterInfo.address}</p>
                </div>
              )}
              {selectedCenterInfo.description && (
                <div>
                  <p className="text-sm text-gray-600">Description</p>
                  <p className="text-gray-800">{selectedCenterInfo.description}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CenterManagement;
